'use client';
import './styles/default.scss';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useStore } from '@/store/store';
import { toSnakeCase } from '@/tracking/amplitude/utils';
import { classes } from '@/utils/classes/classes';
import { parseGamesList } from '@/logic/games/parse-games-list';
import { useGlobalContext } from '@/store/contexts/global-context';
import type { Game } from '@/types/games';
import { CollectionZoneClient } from '../zones/layouts/collection/collection-zone-client';
import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { GameFactoryClient } from '../game/game-factory.client';

type BonusSpinsGameTileComponentProps = {
    games: Array<Game>;
    listType: unknown;
};

export const BonusSpinsGameTileComponent: FC<BonusSpinsGameTileComponentProps> = ({ games, listType }) => {
    const { isMobile, region } = useGlobalContext();
    const { sendEvent } = useStore((state) => state.tracking);
    const gridClassName = classes('grid-zone-layout', COLLECTION_ZONE_LAYOUT[PRISMIC_ZONE_LAYOUT.ONE_ROW]);
    const layout = PRISMIC_ZONE_LAYOUT.ONE_ROW;

    const { games: adaptedGames } = useMemo(
        () => parseGamesList(games ?? [], layout, { region, isMobile }),
        [games, region, isMobile]
    );

    const handleClickCapture = useCallback(
        (event: React.SyntheticEvent) => {
            if (!(event.target instanceof HTMLElement)) {
                return;
            }

            const anchor = event.target.closest<HTMLAnchorElement>('a.game__url');
            if (!anchor) {
                return;
            }

            sendEvent([
                {
                    destination: 'amplitude',
                    eventProps: {
                        event: 'Bonus Spins Details Game Clicked',
                        Module: 'bonus_spins_details',
                        'Event Category': 'bonus_spins_details',
                        'Link URL': anchor.getAttribute('href') || '',
                        'Link Text': toSnakeCase(anchor.textContent || 'game')
                    }
                }
            ]);
        },
        [sendEvent]
    );

    const gameElements = adaptedGames.map((game, index) => (
        <GameFactoryClient
            key={game.uid}
            game={game}
            layout={layout}
            hideGameInfo={true}
            tracking={{
                gamePosition: index + 1
            }}
        />
    ));

    return (
        <div
            className={classes('bonus-spins-game-tiles', `bonus-spins-game-tiles--${listType}`)}
            onClickCapture={handleClickCapture}>
            <CollectionZoneClient
                gameElements={gameElements}
                gameUids={adaptedGames.map((g) => g.uid)}
                shouldApplyDynamicSorting={false}
                isMobile={isMobile}
                isAnimated={false}
                gridClassName={gridClassName ?? ''}
            />
        </div>
    );
};
